import Config from '@/shared/Config'

const GREETING_PROMPT = 'Hello'
const STORAGE_KEY = 'ai_agent_messages'
const NAV_PENDING_KEY = 'ai_agent_nav_pending'

function persistMessages (messages) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(messages))
  } catch (_) { /* quota exceeded — ignore */ }
}

function restoreMessages () {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (raw) {
      let msgs = JSON.parse(raw)
      // Clean up artifacts from interrupted streams (cross-module navigation):
      // - Remove trailing error messages (caused by SSE stream dying)
      // - Remove trailing empty assistant messages (incomplete stream)
      while (msgs.length > 0) {
        const last = msgs[msgs.length - 1]
        if (last.role === 'assistant' && (!last.content || last.content.startsWith('Error:'))) {
          msgs = msgs.slice(0, -1)
        } else {
          break
        }
      }
      return msgs
    }
  } catch (_) { /* corrupted — ignore */ }
  return []
}

export function createChatClient (sessionId) {
  // Only restore messages if the agent initiated a cross-module navigation.
  // On manual refresh or first load, start fresh.
  const isAgentNavigation = !!sessionStorage.getItem(NAV_PENDING_KEY)
  let messages = isAgentNavigation ? restoreMessages() : []
  if (!isAgentNavigation) {
    // Clear stale messages from a previous session
    sessionStorage.removeItem(STORAGE_KEY)
  }
  let isLoading = false
  let abortController = null
  let greetingDone = false
  let baseUrl = null
  let _onChange = null

  function notify () {
    if (_onChange) _onChange({ messages, isLoading })
    persistMessages(messages)
  }

  async function getAgentUrl () {
    if (!baseUrl) {
      if (process.env.VUE_APP_AGENT_URL) {
        baseUrl = process.env.VUE_APP_AGENT_URL.replace(/\/+$/, '')
      } else {
        const config = await Config()
        if (!config.BASE_URL) {
          await new Promise(resolve => setTimeout(resolve, 500))
          return getAgentUrl()
        }
        baseUrl = config.BASE_URL + '/ai-agent'
      }
    }
    return baseUrl
  }

  async function cancelRequest () {
    if (abortController) {
      abortController.abort()
      abortController = null
    }
    messages = messages.filter(
      (m) => !(m.role === 'assistant' && !m.content)
    )
    isLoading = false
    notify()
    try {
      const url = await getAgentUrl()
      await fetch(`${url}/chat/cancel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ session_id: sessionId })
      })
    } catch (_) {
      // Best-effort — ignore network errors on cancel
    }
  }

  async function streamChat (text, opts) {
    if (!opts?.hidden) {
      messages = [...messages, { role: 'user', content: text }]
    }
    isLoading = true
    notify()

    const abort = new AbortController()
    abortController = abort

    try {
      const url = await getAgentUrl()

      const body = {
        message: text,
        session_id: sessionId,
        stream: true
      }
      if (opts?.executionMode) {
        body.execution_mode = opts.executionMode
      }

      const response = await fetch(`${url}/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        signal: abort.signal,
        body: JSON.stringify(body)
      })

      if (!response.ok) {
        const err = await response.json()
        throw new Error(err.error || `HTTP ${response.status}`)
      }

      const reader = response.body?.getReader()
      if (!reader) throw new Error('No response body')

      const decoder = new TextDecoder()
      let assistantContent = ''
      let steps = []

      messages = [
        ...messages,
        { role: 'assistant', content: '', steps: [] }
      ]
      notify()

      const updateAssistantMessage = () => {
        const updated = [...messages]
        updated[updated.length - 1] = {
          role: 'assistant',
          content: assistantContent,
          steps: [...steps]
        }
        messages = updated
        notify()
      }

      let buffer = ''
      let navigated = false
      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() || ''

        for (const line of lines) {
          const trimmed = line.trim()
          if (!trimmed.startsWith('data: ')) continue

          const jsonStr = trimmed.slice(6)
          if (!jsonStr) continue

          try {
            const chunk = JSON.parse(jsonStr)

            if (chunk.error) {
              throw new Error(chunk.error)
            }

            if (chunk.type === 'step') {
              steps = [
                ...steps,
                {
                  step: chunk.step,
                  tool: chunk.tool || undefined,
                  toolError: chunk.tool_error || undefined,
                  detail: chunk.detail || undefined,
                  timestamp: Date.now()
                }
              ]
              if (chunk.tool === 'call_tool' && chunk.detail && chunk.detail.includes('ui.navigate')) {
                navigated = true
              }
              updateAssistantMessage()
            } else {
              assistantContent += chunk.text
              updateAssistantMessage()
              if (chunk.is_final) {
                reader.cancel()
                return
              }
            }
          } catch (e) {
            if (e instanceof SyntaxError) continue
            throw e
          }
        }
      }

      if (!assistantContent && navigated) {
        assistantContent = 'Navigation effectuée.'
        updateAssistantMessage()
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        return
      }
      // Ignore stream errors caused by agent-initiated cross-module navigation
      // (the page is about to reload — no point showing an error)
      if (sessionStorage.getItem('ai_agent_nav_pending')) {
        return
      }
      const errorMsg = error instanceof Error ? error.message : 'Unknown error'
      messages = [
        ...messages.filter(
          (m) => m.role !== 'assistant' || m.content !== ''
        ),
        { role: 'assistant', content: `Error: ${errorMsg}` }
      ]
      notify()
    } finally {
      abortController = null
      isLoading = false
      notify()
    }
  }

  async function sendMessage (text, opts) {
    await streamChat(text, opts)

    // Auto-retry once on provider errors (e.g. "Error in input stream").
    // The backend marks the session dirty on cancel, so the retry gets
    // a fresh provider session.
    const lastMsg = messages[messages.length - 1]
    if (lastMsg && lastMsg.role === 'assistant' && lastMsg.content.startsWith('Error:') && !opts?._retried) {
      console.debug('[AI Agent] Provider error detected, retrying with fresh session')
      // Remove the error message
      messages = messages.filter(m => m !== lastMsg)
      notify()
      // Cancel to force backend to mark session dirty
      await cancelRequest()
      await new Promise(resolve => setTimeout(resolve, 300))
      await streamChat(text, { ...opts, _retried: true })
    }
  }

  function triggerGreeting () {
    if (!greetingDone) {
      greetingDone = true
      streamChat(GREETING_PROMPT, { hidden: true })
    }
  }

  return {
    onChange (fn) {
      _onChange = fn
      // Immediately notify with restored messages (if any)
      if (messages.length > 0) notify()
    },
    sendMessage,
    cancelRequest,
    triggerGreeting
  }
}
